
import { useQuery } from '@tanstack/react-query';
import { DatePicker, Divider, Table, TableProps, Tag } from 'antd';
import dayjs from 'dayjs';
import { useMedia } from 'hooks';
import { IForemanInfo, IKeys, IUserRoles, IWork, IWorkState } from 'models';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { COMPLETED_ROUTE } from 'routes/consts';
import { workService } from 'services';
import { userStore } from 'stores';
import { convertTime } from 'utils/convertTime';
import { getWorkNumber } from 'utils/get-work-number';

interface TableTotal {
	hours: number,
	total: number,
	tips: number
}

const { Summary: { Cell, Row } } = Table;

const columns: TableProps<IWork>['columns'] = [{
	title: '№',
	dataIndex: ['id'],
	key: 'id',
	render: (_, record) => <Tag color="blue">{getWorkNumber(record)}</Tag>
},{
	title: 'Date',
	dataIndex: ['acf', 'date'],
	key: 'date',
	render: date => dayjs(date).format('MM/DD')
},{
	title: 'Hours',
	dataIndex: ['acf', 'foreman_info', 'total_time'],
	key: 'total_time',
	render: text => convertTime(text)
},{
	title: 'Total',
	dataIndex: ['acf', 'foreman_info', 'total'],
	key: 'total',
	render: text => `$${text}`
},{
	title: 'Tips',
	dataIndex: ['acf', 'foreman_info', 'tips'],
	key: 'tips',
	render: text => text && `$${text}`
},{
	title: 'Income',
	dataIndex: ['acf', 'foreman_info'],
	key: 'income',
	render: (record: IForemanInfo) => `$${record.total - record.tips}`
},{
	title: 'Foreman',
	dataIndex: ['acf', 'foreman_info'],
	key: 'foreman',
	render: (record: IForemanInfo) => userStore.getUser(record.workers?.find(el => el.worker_role === IUserRoles.FOREMAN)?.worker)?.name
}];

export const AllCompletedWorks = () => {
	const navigate = useNavigate();
	const { isMobile } = useMedia();
	const [searchParams, setSearchParams] = useSearchParams();

	const getInitialDate = (param: string, fallback: dayjs.Dayjs) => {
		const value = searchParams.get(param);
		const parsed = value ? dayjs(value, 'YYYY-MM-DD') : null;
		return parsed?.isValid() ? parsed : fallback;
	};

	const [startDay, setStartDay] = useState(() =>
		getInitialDate('startd', dayjs().startOf('month'))
	);
	const [endDay, setEndDay] = useState(() =>
		getInitialDate('endd', dayjs().endOf('month'))
	);

	useEffect(() => {
		setSearchParams(
			(prev) => {
				prev.set('startd', startDay.format('YYYY-MM-DD'));
				prev.set('endd', endDay.format('YYYY-MM-DD'));
				return prev;
			},
			{ replace: true }
		);
	}, [startDay, endDay, setSearchParams]);
	
	const { isLoading, data } = useQuery({
		queryFn: () => workService.getAll({
			state: IWorkState.COMPLETED,
			per_page: 200,
			sortbydate: true,
			startd: startDay.format('YYYY-MM-DD'),
			endd: endDay.format('YYYY-MM-DD')
		}),
		queryKey: [IKeys.WORKS, { state: IWorkState.COMPLETED, startDay, endDay }],
		enabled: dayjs.isDayjs(startDay) && dayjs.isDayjs(endDay)
	});

	return (
		<div className="completed-works">
			<DatePicker.RangePicker
				value={[startDay, endDay]}
				onChange={(dates) => {
					if(Array.isArray(dates)) {
						dayjs.isDayjs(dates[0]) && setStartDay(dates[0]);
						dayjs.isDayjs(dates[1]) && setEndDay(dates[1]);
					}
				}}
				size="large"
				format="MM/DD/YYYY"
				inputReadOnly
				className="completed-works"
				showTime
			/>
			<Divider size="small" />
			<div style={{
				paddingLeft: isMobile ? 45 : 0
			}}
			>
				<Table<IWork>
					loading={isLoading}
					size="small"
					columns={columns}
					dataSource={data?.data}
					rowKey={record => record.id}
					pagination={false}
					onRow={(data) => ({
						onClick: () => {
							navigate(`${COMPLETED_ROUTE}/${data.id}`);
						}
					})}
					summary={pageData => {
						const res = pageData.reduce<TableTotal>((acc, { acf: { foreman_info: { total, tips, total_time } } }) => {
							acc.total += Number(total);
							acc.hours += Number(total_time);
							acc.tips += Number(tips);
							return acc;
						}, { total: 0, hours: 0, tips: 0 });

						return (
							<Row>
								<Cell index={0}>{pageData.length}</Cell>
								<Cell index={1} />
								<Cell index={2}>{convertTime(res.hours)}</Cell>
								<Cell index={3}>{`$${res.total}`}</Cell>
								<Cell index={4}>{`$${res.tips}`}</Cell>
								<Cell index={5}>{`$${res.total - res.tips}`}</Cell>
							</Row>
						);
					}}
				/>
			</div>
		</div>
	);
};
